import { useEffect, useState } from 'react';
import {
  CALL_POLL_MS, CALLS_BRIDGE_PATH, Size, bridgedCallOf, callNoteLine, callPath, callPollDone, callStateLine, isCallEnded, t,
  type BridgeOffer, type BridgedCallView,
} from '@bms2/shared';
import { Button } from '../../components/controls';
import { get, post } from './api';
import styles from './OfficeCall.module.css';

/**
 * Call Through the Office Number beside + Log Contact on a Person and a Company (piece 35, *Calls through the office
 * number*; decision 5130). The website's own Call is a link inside the contact rows, so the office call stands in the
 * banner's row instead: the server rings the viewer's own Mobile line first, then the record's number from the
 * company number. The call's state is read every two seconds until the call has ended and its note is written, and
 * the page then reads its notes again. Nothing is drawn where the viewer is not offered the call.
 */
export interface OfficeCallProps {
  recordType: 'PersonRecord' | 'CompanyRecord';
  recordId: string;
  apiBaseUrl: string;
  onWritten: () => void;
  onError: (message: string) => void;
  /** The suites shorten the two seconds. */
  pollMs?: number;
}

/** 🔨 Polls after the call ends before the row stops waiting for its note, as the phone's page does. */
const NOTE_POLLS_MAX = 75;

export function OfficeCallButton({ recordType, recordId, apiBaseUrl, onWritten, onError, pollMs = CALL_POLL_MS }: OfficeCallProps) {
  const [offered, setOffered] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [call, setCall] = useState<BridgedCallView | null>(null);

  useEffect(() => {
    const abort = new AbortController();
    void get<BridgeOffer>(CALLS_BRIDGE_PATH, apiBaseUrl, abort.signal).then((read) => {
      if (!abort.signal.aborted) setOffered(read.ok && read.data.offered === true);
    });
    return () => abort.abort();
  }, [apiBaseUrl]);

  const callId = call?.id ?? null;
  const done = call ? callPollDone(call) : true;
  useEffect(() => {
    if (!callId || done) return undefined;
    let stopped = false;
    let afterEnd = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      const read = await get<unknown>(callPath(callId), apiBaseUrl);
      if (stopped) return;
      const next = read.ok ? bridgedCallOf(read.data) : null;
      if (next) {
        setCall(next);
        if (callPollDone(next)) {
          if (next.noteId) onWritten();
          return;
        }
        if (isCallEnded(next.status) && (afterEnd += 1) >= NOTE_POLLS_MAX) return;
      }
      timer = setTimeout(() => void tick(), pollMs);
    };
    timer = setTimeout(() => void tick(), pollMs);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
    // 🔨 One loop per call: each answer redraws the row without starting a second.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, done, apiBaseUrl, pollMs]);

  if (!offered) return null;
  const place = async () => {
    setPlacing(true);
    const placed = await post<unknown>(CALLS_BRIDGE_PATH, { recordType, recordId, contactMethodId: null }, apiBaseUrl);
    setPlacing(false);
    const view = placed.ok ? bridgedCallOf(placed.data) : null;
    if (view) setCall(view);
    else onError(placed.ok ? t('calls.place.refused') : placed.message);
  };
  const live = call !== null && !callPollDone(call);
  const note = call ? callNoteLine(call) : null;
  return (
    <span className={styles.officeCall} data-office-call="true">
      <Button size={Size.Small} text={[t('calls.office.button')]} actions={[() => void place()]} {...(placing || live ? { disabled: true } : {})} />
      {call ? (
        <span className={styles.state} data-office-call-state={call.status}>
          {[callStateLine(call), note].filter(Boolean).join(' ')}
        </span>
      ) : null}
    </span>
  );
}
